import React, { useMemo } from "react";
import PropTypes from "prop-types";
// consts
import {
  CYLINDER_STATE,
  CYLINDER_PRESSURE_MAP,
  GHOST_CYLINDER_STATE,
} from "salvus-shared/consts/cylinder";
import { COLORS } from "salvus-shared/consts/colors";
// components
import { Popup } from "semantic-ui-react";
import DeliveryIcon from "app/_assets/icon/delivery-icon/DeliveryIcon";
import QuestionIcon from "app/_assets/icon/question-icon/QuestionIcon";
import PendingIcon from "app/_assets/icon/pending-icon/PendingIcon";
// utils
import _ from "lodash";
// styles
import styles from "./CylinderIcon.module.scss";

const { REQUESTED, PENDING, FULL, CURRENT, EMPTY } = CYLINDER_STATE;
const {
  ALERT_SECONDARY, // amarelo
  ALERT_POSITIVE, // verde claro
  NEGATIVE_VARIANT, // vermelho escuro
  SECONDARY_VARIANT_DARK, // cinza claro
  PRIMARY_VARIANT, // Conza escuro
  WHITE_VARIANT, // branco
} = COLORS;

const ICONS = {
  [REQUESTED]: <DeliveryIcon fill="#202124" size="22" />,
  [PENDING]: <PendingIcon fill="#202124" size="22" />,
};

const ICONS_GHOST = {
  [GHOST_CYLINDER_STATE.CURRENT]: <QuestionIcon fill="#202124" size="22" />,
  [GHOST_CYLINDER_STATE.EMPTY]: <QuestionIcon fill="#202124" size="22" />,
  [GHOST_CYLINDER_STATE.FINISHED]: <QuestionIcon fill="#202124" size="22" />,
};

const LABELS = {
  [REQUESTED]: "Solicitação agendada",
  [PENDING]: "Solicitação pendente",
  [FULL]: "Cheio",
  [EMPTY]: "Vazio",
};

const LABELS_GHOST = {
  [CURRENT]: "Pendente",
  [EMPTY]: "Pendente Consumido",
};

function CylinderIcon ({
  pressure,
  state,
  ghostState,
  cylinderSize,
  small,
  cylinderMode,
}) {
  const isGhostStateDefined = !_.isNil(ghostState);
  const isGhostMode = cylinderMode === 'ghost';
  const isCommonMode = cylinderMode === 'common';
  const isNotFinished = ghostState !== GHOST_CYLINDER_STATE.FINISHED;

  const isValidGhostCylinder =
    isGhostStateDefined && isNotFinished && (isGhostMode || !isCommonMode);

  const pressureConfig = useMemo(() => {
    const config = CYLINDER_PRESSURE_MAP[cylinderSize];
    if (!config) return null;

    const { p: maxPressure } = config;

    return {
      maxPressure,
      yellowThreshold: maxPressure * 0.7,
      redThreshold: maxPressure * 0.45,
    };
  }, [cylinderSize]);

  const getPressureStyle = useMemo(() => {
    if (!pressureConfig) {
      return { height: '0%', color: ALERT_POSITIVE, isMax: false };
    }

    const { maxPressure, yellowThreshold, redThreshold } = pressureConfig;

    const clampedPressure = Math.min(Math.max(pressure, 50), maxPressure);
    const heightPercent = Math.round((clampedPressure / maxPressure) * 100);
    const height = `${Math.min(100, Math.max(0, heightPercent))}%`;

    let color = ALERT_POSITIVE;
    if (clampedPressure < redThreshold) {
      color = NEGATIVE_VARIANT;
    } else if (clampedPressure < yellowThreshold) {
      color = ALERT_SECONDARY;
    }

    return { height, color, isMax: heightPercent === 100 };
  }, [pressure, pressureConfig]);

  const renderBar = (height, color, isMax = false, icon) => (
    <div
      className={`${styles.pressureBar} ${small ? styles.small : ''} ${
        isMax ? styles.withTopBorder : styles.withoutTopBorder
      }`}
    >
      <div className={styles.circle}>
        <div className={styles.contentCircle}>
          <div className={styles.mask} style={{ height, backgroundColor: color }} />
          <div className={styles.icon}>{icon}</div>
        </div>
      </div>
    </div>
  );

  const getCylinderVisual = (currentState, isGhost = false) => {
    const numericState = Number(currentState);
    const labels = isGhost ? LABELS_GHOST : LABELS;
    const icons = isGhost ? ICONS_GHOST : ICONS;

    switch (numericState) {
      case CURRENT: {
        const { height, color, isMax } = getPressureStyle;
        return {
          label: `${pressure}`,
          element: renderBar(height, color, isMax, icons[currentState]),
        };
      }
      case FULL:
        return {
          label: labels[FULL],
          element: renderBar(30, PRIMARY_VARIANT, true, icons[FULL]),
        };
      case EMPTY:
        return {
          label: labels[EMPTY],
          element: renderBar(0, WHITE_VARIANT, false, icons[EMPTY]),
        };
      case REQUESTED:
        return {
          label: labels[REQUESTED],
          element: renderBar(0, WHITE_VARIANT, false, icons[REQUESTED]),
        };
      case PENDING:
        return {
          label: labels[PENDING],
          element: renderBar(0, ALERT_POSITIVE, false, icons[PENDING]),
        };
      default:
        return {
          label: 'Desconhecido',
          element: renderBar(0, SECONDARY_VARIANT_DARK, false, icons[EMPTY]),
        };
    }
  };

  const { label, element } = isValidGhostCylinder
    ? getCylinderVisual(ghostState, true)
    : getCylinderVisual(state, false);

  return (
    <div className={styles.wrapper}>
      <Popup trigger={element} content={label} size="tiny" inverted />
    </div>
  );
};
CylinderIcon.defaultProps = {
  small: false,
};

CylinderIcon.propTypes = {
  pressure: PropTypes.number,
  state: PropTypes.number,
  cylinderSize: PropTypes.number,
  small: PropTypes.bool,
};

export default CylinderIcon;
